import Link from 'next/link';
import { SiteSearchResult } from '@/hooks/useSiteSearch';
import styles from '../searchBar.module.scss';

type Props = {
  result: SiteSearchResult;
  isActive: boolean;
  onSelect: (result: SiteSearchResult) => void;
  onMouseEnter: () => void;
};

const renderHighlightedText = (
  text: string,
  matches: SiteSearchResult['matches'],
  key: 'title' | 'description'
) => {
  const match = matches?.find((m) => m.key === key);

  if (!match || !match.indices.length) {
    return text;
  }

  const segments: React.ReactNode[] = [];
  let lastIndex = 0;

  match.indices.forEach(([start, end], i) => {
    if (start > lastIndex) {
      segments.push(text.slice(lastIndex, start));
    }
    segments.push(
      <mark key={i} className={styles.highlight}>
        {text.slice(start, end + 1)}
      </mark>
    );
    lastIndex = end + 1;
  });

  if (lastIndex < text.length) {
    segments.push(text.slice(lastIndex));
  }

  return segments;
};

const SearchResultItem = ({
  result,
  isActive,
  onSelect,
  onMouseEnter,
}: Props) => {
  const { item, matches } = result;

  return (
    <Link
      href={item.href}
      className={`${styles.resultItem} ${isActive ? styles.resultItemActive : ''}`}
      onClick={() => onSelect(result)}
      onMouseEnter={onMouseEnter}
      role="option"
      aria-selected={isActive}
    >
      <div className={styles.resultItemContent}>
        <div className={styles.resultItemHeader}>
          <span className={styles.resultItemTitle}>
            {renderHighlightedText(item.title, matches, 'title')}
          </span>
          <span className={styles.resultItemCategory}>{item.category}</span>
        </div>
        <p className={styles.resultItemDescription}>
          {renderHighlightedText(item.description, matches, 'description')}
        </p>
      </div>
    </Link>
  );
};

export default SearchResultItem;
